import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Download, ArrowRight } from "lucide-react";

const ASSETS = [
  {
    section: "The Monogram",
    tagline: "The interlocking L·S·C — the firm's private mark.",
    items: [
      { label: "Primary — Navy on Transparent", file: "lsc-monogram-hd.png", bg: "linen", preview: "/img/lsc-monogram.png", size: "2400px PNG" },
      { label: "Square — 1200×1200 for Favicon / Avatar", file: "lsc-monogram-1200.png", bg: "linen", preview: "/brand/lsc-monogram-1200.png", size: "1200×1200 PNG" },
      { label: "Reverse — Ivory on Navy", file: "lsc-monogram-reverse.png", bg: "navy", preview: "/brand/lsc-monogram-reverse.png", size: "PNG" },
      { label: "Monochrome — Black (Faxes, Engraving)", file: "lsc-monogram-mono.png", bg: "linen", preview: "/brand/lsc-monogram-mono.png", size: "PNG" },
      { label: "Vector SVG (Bodoni Moda Bold rendition)", file: "lsc-monogram-vector.svg", bg: "linen", preview: "/brand/lsc-monogram-vector.svg", size: "SVG" },
    ],
  },
  {
    section: "The Wordmark",
    tagline: "LONE STAR ★ COUNSEL — Bodoni Moda Bold.",
    items: [
      { label: "Primary — Navy on Transparent", file: "lsc-wordmark-hd.png", bg: "linen", preview: "/brand/lsc-wordmark-hd.png", size: "2400px PNG" },
      { label: "Reverse — Ivory on Navy", file: "lsc-wordmark-reverse.png", bg: "navy", preview: "/brand/lsc-wordmark-reverse.png", size: "PNG" },
      { label: "Monochrome — Single-Color Black", file: "lsc-wordmark-mono.png", bg: "linen", preview: "/brand/lsc-wordmark-mono.png", size: "PNG" },
      { label: "Vector SVG", file: "lsc-wordmark.svg", bg: "linen", preview: "/brand/lsc-wordmark.svg", size: "SVG" },
    ],
  },
  {
    section: "The Full Lockup",
    tagline: "Monogram + Wordmark + Tagline + Services strip.",
    items: [
      { label: "Primary — Navy on Transparent", file: "lsc-lockup-hd.png", bg: "linen", preview: "/brand/lsc-lockup-hd.png", size: "2400px PNG" },
      { label: "Reverse — Ivory on Navy", file: "lsc-lockup-reverse.png", bg: "navy", preview: "/brand/lsc-lockup-reverse.png", size: "PNG" },
      { label: "Monochrome — Single-Color Black", file: "lsc-lockup-mono.png", bg: "linen", preview: "/brand/lsc-lockup-mono.png", size: "PNG" },
      { label: "Vector SVG (wordmark portion)", file: "lsc-lockup.svg", bg: "linen", preview: "/brand/lsc-lockup.svg", size: "SVG" },
    ],
  },
];

const FAVICON_SIZES = [512, 192, 180, 96, 64, 48, 32, 16];

const COLORS = [
  { name: "Midnight Navy", hex: "#0A1324", use: "Primary. Backgrounds, headlines, body." },
  { name: "Ivory", hex: "#F7F3EA", use: "Secondary. Reversed backgrounds, cream." },
  { name: "Heritage Gold", hex: "#C9A46A", use: "Accent. Rules, taglines, the Lone Star." },
  { name: "Oxblood", hex: "#6B2D1D", use: "Editorial accent. Italics, pull quotes." },
  { name: "Slate", hex: "#5B636E", use: "Body copy secondary. Metadata." },
];

const TYPE = [
  { face: "Bodoni Moda · Bold 700", role: "Display · Wordmark · H1 / H2", sample: "Counsel for Business." },
  { face: "Cormorant Garamond · 500 / Italic", role: "Editorial accents · Lede · Pullquotes", sample: "Planning today. Protecting tomorrow.\u00ae" },
  { face: "Outfit · 500", role: "Sans-serif · Kickers, taglines, metadata", sample: "ATTORNEYS · CPA · BUSINESS ADVISOR" },
];

export default function BrandKit() {
  return (
    <div className="bg-ivory" data-testid="brand-kit-page">
      <SEO
        title="Brand Kit — Lone Star Counsel"
        description="Official brand assets, logos, colors, and typography for Lone Star Counsel."
        path="/brand"
      />

      {/* Hero */}
      <section className="panel-navy py-32 md:py-40" data-testid="brand-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker kicker-ivory">Brand Kit</div>
          <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95] tracking-tight text-ivory">
            The firm&apos;s mark, <br/>
            <span className="italic font-cormorant text-brass">and its dressing.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-cormorant italic text-2xl text-ivory/85 leading-snug">
            Every mark, palette entry, and typeface the firm uses — organized, downloadable,
            and ready for letterhead, folders, engagement kits, or referring counsel.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <a
              href="/brand/LoneStarCounsel-BrandKit.zip"
              download
              className="btn-gold inline-flex items-center gap-3"
              data-testid="brand-download-all-cta"
            >
              <Download size={18} />
              Download the Full Kit
              <span className="text-[10px] uppercase tracking-[0.28em] opacity-80 ml-2 border-l border-navy/30 pl-3">
                22 files &nbsp;·&nbsp; ZIP 741 KB
              </span>
            </a>
            <div className="text-[11px] uppercase tracking-[0.28em] text-ivory/60 font-cormorant italic">
              or download individual assets below
            </div>
          </div>
        </div>
      </section>

      {/* Asset sections */}
      {ASSETS.map((sec) => (
        <section key={sec.section} className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24 hairline-top" data-testid={`brand-section-${sec.section.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex flex-col md:flex-row items-baseline justify-between gap-6 mb-12">
            <div>
              <div className="kicker">{sec.section}</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy">
                {sec.tagline}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {sec.items.map((asset) => (
              <div
                key={asset.file}
                className="group"
                data-testid={`asset-${asset.file}`}
              >
                <div className={`aspect-[16/9] flex items-center justify-center p-8 mb-4 overflow-hidden ${asset.bg === "navy" ? "bg-navy" : "bg-linen"}`}>
                  <img
                    src={asset.preview}
                    alt={asset.label}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="font-display text-lg text-navy">{asset.label}</div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-brass mt-1">{asset.size}</div>
                  </div>
                  <a
                    href={`/brand/${asset.file}`}
                    download
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-oxblood hover:text-navy transition font-medium"
                    data-testid={`download-${asset.file}`}
                  >
                    <Download size={13} /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Favicons */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24 hairline-top" data-testid="brand-favicons">
        <div className="mb-12">
          <div className="kicker">Favicons &amp; App Icons</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy">
            The mark, at every size.
          </h2>
          <p className="mt-4 font-cormorant italic text-lg text-slate2 max-w-2xl">
            Reverse monogram on navy. Ready for browser tabs, home-screen shortcuts, PWA icons,
            and social profile avatars.
          </p>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-6" data-testid="favicon-grid">
          {FAVICON_SIZES.map((sz) => (
            <a
              key={sz}
              href={`/brand/favicon-${sz}.png`}
              download
              className="group text-center"
              data-testid={`favicon-${sz}`}
            >
              <div className="aspect-square bg-navy flex items-center justify-center p-3 mb-3 group-hover:opacity-90 transition">
                <img
                  src={`/brand/favicon-${sz}.png`}
                  alt={`${sz}×${sz}`}
                  style={{
                    width: Math.min(sz, 100),
                    height: Math.min(sz, 100),
                    imageRendering: sz < 64 ? "pixelated" : "auto",
                  }}
                />
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-brass">{sz}×{sz}</div>
              <div className="text-xs font-cormorant italic text-slate2 mt-1">
                {sz === 512 ? "PWA" : sz === 192 ? "Android" : sz === 180 ? "iOS Touch" : sz === 32 ? "Browser Tab" : sz === 16 ? "Legacy" : ""}
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Colors */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24 hairline-top" data-testid="brand-colors">
        <div className="mb-12">
          <div className="kicker">Palette</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy">
            Five colors. Nothing more.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" data-testid="color-grid">
          {COLORS.map((c) => (
            <div key={c.hex} className="flex flex-col" data-testid={`color-${c.hex.slice(1)}`}>
              <div
                className="aspect-square hairline-bottom"
                style={{ background: c.hex }}
              />
              <div className="pt-5">
                <div className="font-display text-xl text-navy">{c.name}</div>
                <div className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-brass">{c.hex}</div>
                <div className="mt-3 font-cormorant italic text-sm text-slate2 leading-snug">{c.use}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Typography */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-24 hairline-top" data-testid="brand-typography">
        <div className="mb-12">
          <div className="kicker">Typography</div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-navy">
            Three faces. One editorial voice.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-10" data-testid="type-grid">
          {TYPE.map((t, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-8 hairline-top pt-8" data-testid={`type-${i}`}>
              <div className="md:col-span-3">
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Face {String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 font-display text-2xl text-navy">{t.face}</div>
                <div className="mt-2 font-cormorant italic text-sm text-slate2">{t.role}</div>
              </div>
              <div className={`md:col-span-9 text-4xl md:text-5xl leading-[1.05] text-navy ${i === 0 ? "font-display font-bold" : i === 1 ? "font-cormorant italic" : "font-sans font-medium tracking-[0.32em] uppercase text-2xl md:text-3xl"}`}>
                {t.sample}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Usage */}
      <section className="panel-navy py-24 md:py-32" data-testid="brand-usage">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">Usage</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-ivory leading-[1.02]">
                A brief on <span className="italic font-cormorant text-brass">using the mark.</span>
              </h2>
            </div>
            <ul className="lg:col-span-6 lg:col-start-7 space-y-6 text-ivory/85 font-light">
              {[
                "The monogram never touches its container. Reserve clear space equal to the width of the star at minimum.",
                "The wordmark and monogram may appear separately or together — but the full lockup is preferred on formal print.",
                "Only the specified palette. No teals, no purples, no rainbows. Discretion is the aesthetic.",
                "Never stretch, skew, or recolor outside the palette. Never place the mark on a busy photograph without a scrim.",
                "For engraving, embroidery, or laser-cutting — use the monochrome variant. For folders, signage, or dark presentations — use the reverse variant.",
              ].map((t, i) => (
                <li key={i} className="hairline-top pt-5 text-base md:text-lg leading-relaxed" data-testid={`usage-${i}`}>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 text-center" data-testid="brand-cta">
        <p className="font-cormorant italic text-2xl md:text-3xl text-slate2 max-w-3xl mx-auto leading-snug">
          To license the mark for co-branded materials or referring counsel — or for higher-resolution
          print files — write to the firm.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary" data-testid="brand-cta-contact">
            Write to the Firm <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
